import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  updateConnectionRequest,
  getStartupOpportunityById 
} from './startup-opportunities';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

const functions = getFunctions();

/**
 * Facilitate a connection between candidate and founder
 * @param {string} requestId - The connection request ID
 * @param {Object} facilitationData - Additional data for facilitation
 * @returns {Promise<Object>} - Success status and result
 */
export const facilitateConnection = async (requestId, facilitationData = {}) => {
  try {
    console.log('Facilitating connection for request:', requestId);

    // Get the connection request details
    const db = getFirestore();
    const requestRef = doc(db, 'connection_requests', requestId);
    const requestSnap = await getDoc(requestRef);

    if (!requestSnap.exists()) {
      return { success: false, error: 'Connection request not found' };
    }

    const requestData = requestSnap.data();

    // Get user profile data
    const userProfileRef = doc(db, 'user_profiles', requestData.userId);
    const userProfileSnap = await getDoc(userProfileRef);
    
    let userData = { 
      name: 'Unknown User', 
      email: 'unknown@example.com' 
    };
    
    if (userProfileSnap.exists()) {
      const profile = userProfileSnap.data();
      userData = {
        name: profile.basicInfo?.name || profile.displayName || 'Unknown User',
        email: profile.basicInfo?.email || profile.email || 'unknown@example.com'
      };
    }

    // Get opportunity data
    const opportunityResult = await getStartupOpportunityById(requestData.opportunityId);
    if (!opportunityResult.success) {
      return { success: false, error: 'Failed to fetch opportunity data' };
    }

    const opportunityData = opportunityResult.data;

    // Get founder email from opportunity data
    const founderEmail = opportunityData.founderInfo?.email || opportunityData.contactEmail;
    if (!founderEmail) {
      return { success: false, error: 'Founder email not found in opportunity data' };
    }

    // Send introduction email
    const sendIntroductionEmail = httpsCallable(functions, 'sendConnectionIntroductionEmail');
    
    const emailData = {
      candidateEmail: userData.email,
      candidateName: userData.name,
      founderEmail: founderEmail,
      founderName: opportunityData.founderInfo?.name || 'Founder',
      companyName: opportunityData.companyName,
      roleArea: opportunityData.roleArea,
      candidateMessage: requestData.userMessage,
      adminNotes: facilitationData.adminNotes || ''
    };

    const emailResult = await sendIntroductionEmail(emailData);
    
    if (!emailResult.data.success) {
      return { success: false, error: 'Failed to send introduction email' };
    }

    // Update connection request status to 'introduced'
    const updateResult = await updateConnectionRequest(requestId, {
      status: 'introduced',
      introducedAt: new Date(),
      facilitatedBy: facilitationData.facilitatedBy || 'admin',
      adminNotes: facilitationData.adminNotes || ''
    });

    if (!updateResult.success) {
      console.warn('Failed to update connection request status, but email was sent');
    }

    return { 
      success: true, 
      message: 'Connection facilitated successfully',
      emailSent: true
    };

  } catch (error) {
    console.error('Error facilitating connection:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to facilitate connection' 
    };
  }
};

/**
 * Send admin notification for new connection request
 * @param {Object} requestData - The connection request data
 * @param {Object} userData - The user data
 * @param {Object} opportunityData - The opportunity data
 * @returns {Promise<Object>} - Success status and result
 */
export const notifyAdminOfNewRequest = async (requestData, userData, opportunityData) => {
  try {
    console.log('Sending admin notification for new connection request');

    const sendNotification = httpsCallable(functions, 'sendConnectionRequestNotification');
    
    const notificationData = {
      candidateName: userData.name || 'Unknown User',
      candidateEmail: userData.email || 'unknown@example.com',
      companyName: opportunityData.companyName || 'Unknown Company',
      roleArea: opportunityData.roleArea,
      candidateMessage: requestData.userMessage,
      requestId: requestData.id
    };

    const result = await sendNotification(notificationData);
    
    return { 
      success: result.data.success, 
      message: result.data.message 
    };

  } catch (error) {
    console.error('Error sending admin notification:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to send admin notification' 
    };
  }
};

/**
 * Get connection request statistics for admin dashboard
 * @returns {Promise<Object>} - Success status and statistics
 */
export const getConnectionStats = async () => {
  try {
    const db = getFirestore();
    
    // This would typically use aggregation queries, but for now we'll use a simple approach
    // In production, consider using Firebase Functions for server-side aggregation
    
    return {
      success: true,
      data: {
        totalRequests: 0,
        pendingRequests: 0,
        approvedRequests: 0,
        introducedRequests: 0,
        declinedRequests: 0
      }
    };
    
  } catch (error) {
    console.error('Error fetching connection stats:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to fetch connection statistics' 
    };
  }
};